import ExcelJS from 'exceljs';
import { STOCK_QUALITY_COLUMNS } from './stockByConditionDisplay';

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Texto legible de una celda ExcelJS */
function adjustmentCellToString(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'number' && Number.isFinite(val)) return String(val);
  if (val instanceof Date) return val.toISOString();
  if (typeof val === 'object') {
    if (typeof val.text === 'string') return val.text;
    if (Array.isArray(val.richText)) return val.richText.map((x) => x.text).join('');
    if (val.result !== undefined) return adjustmentCellToString(val.result);
    if (val.hyperlink && val.text) return String(val.text);
  }
  return String(val);
}

function headerNorm(h) {
  return String(h ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');
}

function buildConteoHeaderToKey() {
  const m = {};
  for (const { key, label } of STOCK_QUALITY_COLUMNS) {
    m[headerNorm(`Conteo_${label}`)] = key;
    m[headerNorm(`Conteo_${label.replace(/\s+/g, '_')}`)] = key;
    m[headerNorm(`conteo_${key}`)] = key;
  }
  return m;
}

const CONTEO_HEADER_TO_KEY = buildConteoHeaderToKey();

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFE0F2F1' },
};

const CONTEO_HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFEF3C7' },
};

/**
 * Exporta la lista de productos con stock de referencia y conteo físico (columnas editables en Excel).
 * @param {Array<object>} rows
 * @param {string} filenameBase
 * @param {{ simpleMode?: boolean }} options
 */
export async function exportAdjustmentGuideExcel(rows, filenameBase = 'Guia_ajuste_inventario', options = {}) {
  const { simpleMode = false } = options;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Ajuste', {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  const stockHdr = STOCK_QUALITY_COLUMNS.map(({ label }) => `Stock_${label.replace(/\s+/g, '_')}`);
  const conteoHdr = STOCK_QUALITY_COLUMNS.map(({ label }) => `Conteo_${label.replace(/\s+/g, '_')}`);

  const headers = simpleMode
    ? ['product_id', 'SKU', 'Producto', 'UM', 'Stock_total', 'Costo_prom', 'Conteo_total']
    : [
        'product_id',
        'SKU',
        'Producto',
        'UM',
        'Stock_total',
        ...stockHdr,
        'Costo_prom',
        ...conteoHdr,
      ];

  const headerRow = ws.addRow(headers);
  headerRow.font = { bold: true };
  headerRow.eachCell((cell, colNumber) => {
    const label = headers[colNumber - 1];
    const isConteo = label.startsWith('Conteo_');
    cell.fill = isConteo ? CONTEO_HEADER_FILL : HEADER_FILL;
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  const num = (v) => {
    const x = parseFloat(v);
    return Number.isFinite(x) ? x : 0;
  };

  for (const r of rows) {
    const loaded = r.stockAtEntry !== null && r.stockAtEntry !== undefined;
    const stockZero = loaded && Math.abs(num(r.stockAtEntry)) < 0.0001;
    const costDisplay = loaded
      ? stockZero
        ? num(r.averageCostManual)
        : num(r.averageCost)
      : '';

    const line = [
      r.product_id,
      r.sku ?? '',
      r.name ?? '',
      r.unit_of_measure ?? 'UN',
      loaded ? num(r.stockAtEntry) : '',
    ];

    if (simpleMode) {
      line.push(costDisplay);
      const totalConteo = r.countedByCondition
        ? STOCK_QUALITY_COLUMNS.reduce((s, { key }) => s + num(r.countedByCondition[key]), 0)
        : '';
      line.push(loaded ? totalConteo : '');
    } else {
      for (const { key } of STOCK_QUALITY_COLUMNS) {
        line.push(loaded && r.stockByCondition ? num(r.stockByCondition[key]) : '');
      }

      line.push(costDisplay);

      for (const { key } of STOCK_QUALITY_COLUMNS) {
        line.push(r.countedByCondition ? r.countedByCondition[key] ?? '' : '');
      }
    }

    ws.addRow(line);
  }

  headers.forEach((h, i) => {
    let w = 13;
    if (h === 'product_id') w = 11;
    if (h === 'SKU') w = 20;
    if (h === 'Producto') w = 40;
    if (h === 'UM') w = 7;
    ws.getColumn(i + 1).width = w;
  });

  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };

  const buf = await wb.xlsx.writeBuffer();
  const stamp = new Date().toISOString().slice(0, 10);
  downloadBuffer(buf, `${filenameBase}_${stamp}.xlsx`);
}

/**
 * Lee un .xlsx exportado desde esta pantalla y devuelve parches de conteo/costo por product_id.
 * Solo se usan columnas Conteo_* y Costo_prom con celda no vacía.
 * @param {{ simpleMode?: boolean }} options
 * @returns {{ updates: Array<{ productId: number, patch: Record<string, string>, averageCostManual: string|null }> }}
 */
export async function parseAdjustmentCountedImport(arrayBuffer, options = {}) {
  const { simpleMode = false } = options;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);
  const ws = wb.worksheets[0];
  if (!ws) {
    throw new Error('El archivo no tiene hojas.');
  }

  const colMap = {};
  const hr = ws.getRow(1);
  hr.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const t = adjustmentCellToString(cell.value).trim();
    if (t) colMap[headerNorm(t)] = colNumber;
  });

  const productIdCol = colMap.product_id;
  if (!productIdCol) {
    throw new Error('Falta la columna product_id. Exporte de nuevo desde el sistema sin eliminar columnas.');
  }
  const costCol = colMap.costo_prom || colMap.costo || null;

  const conteoTotalCol = colMap.conteo_total || null;
  const conteoCols = {};
  if (!simpleMode) {
    for (const [hn, colNum] of Object.entries(colMap)) {
      const k = CONTEO_HEADER_TO_KEY[hn];
      if (k) conteoCols[k] = colNum;
    }
  }
  if (simpleMode && !conteoTotalCol) {
    throw new Error('Falta la columna Conteo_total. Exporte de nuevo en modo ajuste simple.');
  }
  if (!simpleMode && Object.keys(conteoCols).length === 0) {
    throw new Error(
      'No se encontraron columnas de conteo (Conteo_Bueno, Conteo_Dañado, etc.). Use el archivo exportado aquí.'
    );
  }

  const updates = [];
  const maxRow = ws.rowCount || 0;

  for (let ri = 2; ri <= maxRow; ri++) {
    const row = ws.getRow(ri);
    const pidRaw = adjustmentCellToString(row.getCell(productIdCol).value).trim();
    if (!pidRaw) continue;
    const productId = parseInt(pidRaw, 10);
    if (!Number.isFinite(productId)) continue;

    const patch = {};
    if (simpleMode && conteoTotalCol) {
      const raw = row.getCell(conteoTotalCol).value;
      const s = adjustmentCellToString(raw).trim().replace(',', '.');
      if (s !== '') {
        const n = parseFloat(s);
        patch.good = Number.isFinite(n) ? String(n) : '0';
        for (const { key } of STOCK_QUALITY_COLUMNS) {
          if (key !== 'good') patch[key] = '0';
        }
      }
    } else {
      for (const [condKey, colNum] of Object.entries(conteoCols)) {
        const raw = row.getCell(colNum).value;
        const s = adjustmentCellToString(raw).trim().replace(',', '.');
        if (s !== '') {
          const n = parseFloat(s);
          patch[condKey] = Number.isFinite(n) ? String(n) : '0';
        }
      }
    }
    let averageCostManual = null;
    if (costCol) {
      const sCost = adjustmentCellToString(row.getCell(costCol).value).trim().replace(',', '.');
      if (sCost !== '') {
        const nCost = parseFloat(sCost);
        averageCostManual = Number.isFinite(nCost) ? String(nCost) : '0';
      }
    }
    if (Object.keys(patch).length > 0 || averageCostManual !== null) {
      updates.push({ productId, patch, averageCostManual });
    }
  }

  return { updates };
}
